import { useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import WaveSurfer from 'wavesurfer.js';
import { useWaveform } from 'hooks/WaveformHook';
import { MusicPlayerContext } from 'context/MusicPlayer';
import { PlayIcon, PauseIcon } from '@heroicons/react/outline';
import { useAudioPlayer } from 'hooks/audioPlayer';

export default function AudioPlayer() {
  const { playing, setPlaying } = useContext(MusicPlayerContext);
  const [volume, setVolume] = useState(0.7);
  const [trackPlaying, setTrackPlaying] = useState(false);
  const location = useLocation();
  const {
    audio,
    isPlaying,
    pausePlayback,
    playAudio,
    isPaused,
    isStopped,
    resumePlayback,
    stopPlayback,
    qTrack,
    storeInstance,
    clearInstance,
  } = useAudioPlayer();

  const waveformRef = useRef(null);
  const wavesurfer = useRef(null);

  const { buildWaveformOptions } = useWaveform();

  useEffect(() => {
    setPlaying({});
  }, [location?.pathname]);

  useEffect(() => {
    if (playing?.music?.file != null) {
      const formWaveSurferOptions = buildWaveformOptions(waveformRef.current);

      wavesurfer.current = WaveSurfer?.create(formWaveSurferOptions);

      if (playing?.music?.file.startsWith('blob')) {
        wavesurfer.current?.load(playing.music.file);
      } else {
        wavesurfer.current?.load(
          `${playing.music.file.includes('https://') ? '' : 'https://'}${
            playing.music.file
          }`,
        );
      }

      wavesurfer.current?.on('ready', function () {
        if (wavesurfer.current) {
          storeInstance(wavesurfer.current);
          if (playing.music.autoStart) {
            wavesurfer?.current?.play();
            playAudio(playing.music);
            setTrackPlaying(true);
          } else {
            qTrack(playing.music);
            setTrackPlaying(false);
          }
          wavesurfer.current?.setVolume(volume);

          setVolume(volume);
        }
      });

      wavesurfer.current?.on('play', function () {
        resumePlayback();
        setTrackPlaying(true);
      });

      wavesurfer.current?.on('pause', function () {
        pausePlayback();
        setTrackPlaying(false);
      });

      wavesurfer.current?.on('finish', function () {
        wavesurfer?.current?.stop();
        stopPlayback();
        setTrackPlaying(false);
      });

      return () => {
        wavesurfer.current?.destroy();
        clearInstance();
      };
    }
  }, [playing]);

  useEffect(() => {
    wavesurfer.current?.setVolume(volume);
  }, [volume]);

  if (!playing?.music?.file) return <></>;

  return (
    <>
      <footer className="bg-white">
        <div className="mx-auto h-11">
          <div className="grid grid-cols-12 gap-4 p-3 pl-5">
            {playing.music ? (
              <div className="col-span-2">
              <div className="grid grid-cols-4 gap-4">
                <div className="p-1.5">
                 <img className="w-full md:w-72 block rounded" src={playing.music.image?.icon ?? playing.music.image ?? null}></img>
                </div>
                <div className="col-span-3 pt-1.5">
                  {playing.music.title}
                </div>
              </div>
            </div>
            ): <div className="col-span-1"></div>}
            <div ref={waveformRef} className={'w-full col-span-7'} />
            <div className="col-span-3">
              <div className="w-1/2">
                {trackPlaying ? (
                  <PauseIcon
                    className={'h-10 text-red-500'}
                    onClick={() => {
                      if (waveformRef?.current !== null) {
                        wavesurfer?.current?.pause();
                        pausePlayback();
                        setTrackPlaying(false);
                      }
                    }}
                  />
                ) : (
                  <PlayIcon
                    className={'h-10 text-green-500'}
                    onClick={() => {
                      if (waveformRef?.current !== null) {
                        wavesurfer?.current?.play();
                        resumePlayback();
                        setTrackPlaying(true);
                      }
                    }}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}
